// components/UploadImageModal.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import Dropzone from "react-dropzone-uploader";
import { Formik, Form, Field, FieldArray } from "formik";
import * as Yup from "yup";
import { FiCheck, FiCheckCircle, FiUploadCloud, FiX } from "react-icons/fi";
import { ImageCard } from "./ImageCard"; // your existing card

import "./UploadImageModal.scss";
import { FaFileDownload } from "react-icons/fa";
import { TbFileDownload } from "react-icons/tb";
import { isEqual } from "lodash";
import { MediaPageProps } from "../../../types/app/mediaTypes";
import { appConstants } from "../../../redux/action-constants/inapp.constants";
import { AlertMsg } from "../../../shared/components/alert-msg";
import { ActionProps } from "./InAppContent";
import { LoadingItem } from "../../../shared/components/loading";

type Phase = "upload" | "list" | "uploading";
type ImageData = {
    name: string;
    categoryName: string;
    file: File;
    otherNames: string[];
};

interface UploadImageModalProps {
    show: boolean;
    onClose: () => void;
    handleUploadImages: (images: ImageData[], onProgress?: (progress: number) => void) => Promise<{
        isSuccess: boolean;
        isError: boolean;
    }>;
    pageProps: MediaPageProps;
    getProductCategoriesAction: ({ pageProps, payload }: ActionProps) => Promise<void>
}

export type ImgMeta = {
    file: File;
    imageUrl: string;
    name: string;
    fileName: string;
    categoryName: string;
    otherNames: string[];
    createdDate: string;
    size: number;
};


const formSchema = Yup.object({
    name: Yup.string().trim().required("File name is required"),
    categoryName: Yup.string().trim().required("Category is required"),
    otherNames: Yup.array().of(Yup.string().trim().min(1, "Empty value")),
});

const UploadImageModal: React.FC<UploadImageModalProps> = ({ show, getProductCategoriesAction, onClose, handleUploadImages, pageProps }) => {
    const [phase, setPhase] = useState<Phase>("upload");
    const [images, setImages] = useState<ImgMeta[]>([]);
    const [selected, setSelected] = useState<number | null>(0);
    const [loadImgs, setLoadImgs] = useState(0);
    const [categories, setCategories] = useState<{ label: string, value: string }[]>([])
    const [loadingCategories, setLoadingCategories] = useState<boolean>(false)

    // header uploading state
    const [progress, setProgress] = useState(0);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const isUploading = phase === "uploading";

    const dzRef = useRef<any>(null);
    const uploadInFlightRef = useRef(false);

    const handleToggleSelect = (idx: number) => {
        setSelected((prev) => (prev === idx ? null : idx));
    };

    const handleDeleteSelected = () => {
        if (selected === null) return;
        setImages((prev) => prev.filter((_, i) => i !== selected));
        setSelected(null);
        setLoadImgs((v) => v + 1);
    };

    const handleDiscardAll = () => {
        setImages([]);
        setSelected(null);
        setLoadImgs((v) => v + 1);
    };
    useEffect(() => {
        setLoadingCategories(true);
        getProductCategoriesAction({ pageProps, payload: 'Page=1&Size=100' });
    }, []);

    const memoizedCategories = useMemo(() => {
        if (pageProps.getProductCategoriesRequest.request_status === appConstants.GET_STORE_CATEGORIES_SUCCESS) {
            const cats = pageProps.getProductCategoriesRequest?.request_data?.response?.results?.map((cat) => ({
                label: cat.name as string,
                value: cat.name as string,
            })) || [];
            setLoadingCategories(false);
            return cats;
        }
        return categories;
    }, [pageProps.getProductCategoriesRequest.request_status]);

    useEffect(() => {
        setCategories(memoizedCategories);
    }, [memoizedCategories]);

    const selectedMeta = useMemo(
        () => (selected !== null ? images[selected] : null),
        [selected, images]
    );

    const saveSelectedMeta = (values: {
        name: string;
        categoryName: string;
        otherNames: string[];
    }) => {
        if (selected === null) return;
        setImages((prev) => {
            const next = [...prev];
            next[selected] = { ...next[selected], ...values };
            return next;
        });
        setLoadImgs((v) => v + 1);
    };

    const handleDone = async () => {
        if (!images.length || uploadInFlightRef.current) return;

        uploadInFlightRef.current = true;
        setPhase("uploading");
        setProgress(0);
        setUploadError(null);

        try {
            const result = await handleUploadImages(images, (overallProgress) => {
                setProgress(overallProgress); // updates global progress bar
            });

            if (result.isSuccess) {
                onClose();
                return;
            }

            setUploadError("One or more images failed to upload. Please try again.");
            setPhase("list");
        } catch (error) {
            setUploadError("One or more images failed to upload. Please try again.");
            setPhase("list");
        } finally {
            uploadInFlightRef.current = false;
        }
    };


    if (!show) return null;
    const DropzoneInputContent = () => {
        return (
            <div className="dz-input-content">
                <TbFileDownload size={40} className="icon" />
                <p>
                    Drag & Drop or <span className="highlight">Choose file</span> to upload
                </p>
                <small>JPG, PNG, WEBP (Max 5MB)</small>
            </div>
        );
    };
    const EmptyPreview = () => <></>;

    const AutoSave = ({ values, submitForm }: { values: any; submitForm: () => void }) => {
        const firstRender = useRef(true);
        const lastValues = useRef(values);

        useEffect(() => {
            if (firstRender.current) {
                firstRender.current = false;
                lastValues.current = values;
                return;
            }

            // only save if values changed
            if (!isEqual(values, lastValues.current)) {
                submitForm();
                lastValues.current = values;
            }
        }, [values, submitForm]);

        return null;
    }

    const handleRemove = (idx: number) => {
        setImages((prev) => prev.filter((_, i) => i !== idx));
    };
    return (
        <div className={`uim-overlay `}>
            <div className={`uim-modal ${phase === "list" && 'full-width'} ${phase === "uploading" && 'full-width'}`} role="" aria-modal="true">
                {/* HEADER */}
                {isUploading ? (
                    <div className="uim-header uploading">
                        {/* progress bar behind content */}
                        <div
                            className="progress-bar"
                            style={{ width: `${progress}%` }}
                        ></div>

                        <div className="left">
                            <span className="title">
                                {progress < 100 ? "Uploading…" : "Completed"}
                            </span>
                        </div>

                        <div className="right">
                            <button className="btn-done" type="button" disabled>
                                {progress < 100 ? "Uploading…" : "Finishing…"}
                            </button>
                        </div>
                    </div>
                ) : phase === "upload" ? (
                    <div className="uim-header">
                        <div className="left">
                            <span className="title">Upload new image</span>
                        </div>
                        <button className="close-btn" onClick={onClose}>
                            <FiX />
                        </button>

                    </div>
                ) : (
                    <div className="uim-header list">
                        <div className="left">
                            <FiCheck className="ok" />
                            <span className="title">
                                {images.length} file{images.length !== 1 ? "s" : ""} uploaded
                            </span>
                        </div>
                        <div className="right">
                            <button className="btn-ghost" onClick={onClose}>
                                Discard all
                            </button>
                            <button
                                className="btn-done"
                                onClick={handleDone}
                                disabled={
                                    !images.length ||
                                    images.some(
                                        (img) =>
                                            !img.name?.trim() ||
                                            !img.categoryName?.trim()
                                    )
                                }
                            >
                                Done
                            </button>

                        </div>
                    </div>
                )}

                {/* BODY */}
                {phase === "upload" && (
                    <div className="uim-body upload-phase">
                        <div className="dropzone-wrap">
                            <Dropzone
                                ref={dzRef}
                                onChangeStatus={({ file }, status) => {
                                    const dateNow = new Date()
                                    if (status === "done") {
                                        const meta: ImgMeta = {
                                            file,
                                            imageUrl: URL.createObjectURL(file),
                                            fileName: file.name.replace(/\.[^.]+$/, ""),
                                            name: file.name.replace(/\.[^.]+$/, ""),
                                            categoryName: "",
                                            createdDate: dateNow.toISOString(),
                                            size: file.size,
                                            otherNames: [],
                                        };
                                        setImages((prev) => [...prev, meta]);
                                    }
                                }}
                                accept="image/*"
                                inputContent={
                                    images.length === 0 ? <DropzoneInputContent /> : null
                                }

                                maxFiles={100}
                                maxSizeBytes={5242880}
                                multiple
                                canCancel={false}
                                PreviewComponent={EmptyPreview}
                            />

                            {/* Custom Grid */}
                            {images.length > 0 && (
                                <div className="uploaded-list">
                                    {images.map((img, idx) => (
                                        <div key={idx} className="uploaded-file">
                                            <span className="file-name">{img.fileName}</span>
                                            <button
                                                type="button"
                                                className="remove-btn"
                                                onClick={() => handleRemove(idx)}
                                            >
                                                <FiX />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="actions">
                            <button className="btn-cancel" onClick={onClose}>
                                Cancel
                            </button>
                            <button
                                className="btn-upload"
                                disabled={images.length === 0}
                                onClick={() => setPhase("list")}
                            >
                                Upload image
                            </button>
                        </div>
                    </div>
                )}

                {phase !== "upload" && (
                    <div className="uim-body list-phase">
                        {/* LEFT: LIST AREA */}
                        <div className="left-pane">
                            {(uploadError || pageProps.uploadProductImageRequest.request_status === appConstants.UPLOAD_PRODUCT_PICTURE_FAILURE) &&
                                <div style={{ width: '100%' }}>
                                    <AlertMsg
                                        type="error"
                                        message={pageProps.uploadProductImageRequest?.request_data.error || uploadError}
                                    />
                                </div>
                            }
                            <div className="tool-bar">
                                <div className="tool-left">
                                    {selected != null && <div className="unselect" onClick={() => setSelected(null)}><div>-</div></div>}
                                    <span className="selected-count">{selected != null ? '1' : '0'} Selected</span>
                                </div>

                                <div className="tool-actions">
                                    <button className="tool-icon-btn" disabled>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="7 10 12 15 17 10" />
                                            <line x1="12" y1="15" x2="12" y2="3" />
                                        </svg>
                                    </button>
                                    <button className="tool-icon-btn" onClick={() => handleDeleteSelected()} disabled={selected == null}>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="3 6 5 6 21 6" />
                                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m5 0V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            <div className="grid">
                                {images.map((img, idx) => (
                                    // <div className="grid-item" key={`${idx}-${loadImgs}`}>
                                    <ImageCard
                                        key={`${idx}-${loadImgs}`}
                                        image={img}
                                        isSelected={selected === idx}
                                        onSelect={() => handleToggleSelect(idx)}
                                        onEdit={() => { return; }}
                                    />
                                    // </div>
                                ))}
                            </div>
                        </div>

                        {/* RIGHT: FORM PANE */}
                        <div className="right-pane">
                            {selectedMeta ? (

                                <Formik
                                    enableReinitialize
                                    initialValues={{
                                        name: selectedMeta.name,
                                        categoryName: selectedMeta.categoryName || "",
                                        otherNames: selectedMeta.otherNames || [],
                                    }}
                                    validationSchema={formSchema}
                                    onSubmit={(vals) => {
                                        saveSelectedMeta(vals);
                                    }}
                                >
                                    {({ values, errors, touched, submitForm }) => (
                                        <Form className="side-form" onBlur={(e) => e.stopPropagation()}>
                                            {/* Auto-save component */}
                                            <AutoSave values={values} submitForm={submitForm} />

                                            <div className="field">
                                                <label>File name</label>
                                                <Field name="name" placeholder="Jute Leaves" />
                                                {touched.name && errors.name && (
                                                    <div className="error-text">{errors.name}</div>
                                                )}
                                            </div>

                                            <div className="field">
                                                <label>Category</label>
                                                <Field as="select" name="categoryName">
                                                    {!loadingCategories && <option value="">Select categories</option>}
                                                    {loadingCategories && <option value="" disabled><LoadingItem /></option>}
                                                    {!loadingCategories && categories.map((c) => (
                                                        <option key={c.value} value={c.value}>
                                                            {c.label}
                                                        </option>
                                                    ))}
                                                </Field>
                                                {touched.categoryName && errors.categoryName && (
                                                    <div className="error-text">{errors.categoryName as string}</div>
                                                )}
                                            </div>

                                            <div className="field">
                                                <label>Other names</label>
                                                <FieldArray name="otherNames">
                                                    {({ push, remove }) => (
                                                        <>
                                                            <div className="chips">
                                                                {values.otherNames.map((name, index) => (
                                                                    <span className="chip" key={index}>
                                                                        {name}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => remove(index)}
                                                                            aria-label="Remove"
                                                                        >
                                                                            ×
                                                                        </button>
                                                                    </span>
                                                                ))}
                                                                <input
                                                                    type="text"
                                                                    placeholder="Add other names"
                                                                    onKeyDown={(e) => {
                                                                        const el = e.currentTarget;
                                                                        if (e.key === "Enter" && el.value.trim()) {
                                                                            e.preventDefault();
                                                                            push(el.value.trim());
                                                                            el.value = "";
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                            {touched.otherNames &&
                                                                typeof errors.otherNames === "string" && (
                                                                    <div className="error-text">{errors.otherNames}</div>
                                                                )}
                                                        </>
                                                    )}
                                                </FieldArray>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            ) : (
                                <div className="empty-tip">Select an image to edit its details.</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UploadImageModal;
